import type { ApiClient } from '../../../shared/api/api-client';
import type { AdminTrainerRow, TrainerBranchPlacement } from './trainers.types';

function asStr(v: unknown): string {
  if (v == null) return '';
  return String(v).trim();
}

export function parsePlacement(raw: unknown): TrainerBranchPlacement | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  const id = typeof r.id === 'number' ? r.id : Number(r.id);
  const branch_id = typeof r.branch_id === 'number' ? r.branch_id : Number(r.branch_id);
  const branch_name = typeof r.branch_name === 'string' ? r.branch_name.trim() : '';
  if (!Number.isFinite(id) || id < 1 || !Number.isFinite(branch_id) || branch_id < 1) return null;
  return { id, branch_id, branch_name };
}

export function parseAdminTrainerRow(raw: unknown): AdminTrainerRow | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  const id = typeof r.id === 'number' ? r.id : Number(r.id);
  if (!Number.isFinite(id) || id < 1) return null;
  const placementsRaw = r.branch_placements;
  const branch_placements: TrainerBranchPlacement[] = [];
  if (Array.isArray(placementsRaw)) {
    for (const p of placementsRaw) {
      const pl = parsePlacement(p);
      if (pl) branch_placements.push(pl);
    }
  }
  const email = asStr(r.email);
  const full_name = r.full_name == null || r.full_name === '' ? null : asStr(r.full_name);
  const phone = r.phone == null || r.phone === '' ? null : asStr(r.phone);
  const description = r.description == null || r.description === '' ? null : asStr(r.description);
  const photo_url = r.photo_url == null || r.photo_url === '' ? null : asStr(r.photo_url);
  const specialization =
    r.specialization == null || r.specialization === '' ? null : asStr(r.specialization);
  const description_format = asStr(r.description_format) || 'markdown';
  const created_at = typeof r.created_at === 'string' ? r.created_at : undefined;
  return {
    id,
    email,
    full_name,
    phone,
    is_active: Boolean(r.is_active),
    created_at,
    photo_url,
    description,
    description_format,
    specialization,
    branch_placements,
    created_via: typeof r.created_via === 'string' ? r.created_via : null,
    created_by_user_id: typeof r.created_by_user_id === 'number' ? r.created_by_user_id : null,
    created_by_name: typeof r.created_by_name === 'string' ? r.created_by_name : null,
    temporary_password: typeof r.temporary_password === 'string' ? r.temporary_password : null
  };
}

function normalizeListPayload(raw: unknown): { items: AdminTrainerRow[]; total: number } {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return { items: [], total: 0 };
  }
  const o = raw as Record<string, unknown>;
  const totalRaw = o.total;
  const total = typeof totalRaw === 'number' && Number.isFinite(totalRaw) ? totalRaw : Number(totalRaw);
  const totalNorm = Number.isFinite(total) && total >= 0 ? total : 0;
  const itemsRaw = o.items;
  if (!Array.isArray(itemsRaw)) return { items: [], total: totalNorm };
  const items: AdminTrainerRow[] = [];
  for (const row of itemsRaw) {
    const p = parseAdminTrainerRow(row);
    if (p) items.push(p);
  }
  return { items, total: totalNorm || items.length };
}

export type FetchAdminTrainersParams = {
  q?: string;
  limit?: number;
  offset?: number;
  /** Явный контекст орг. для СА (`?organization_id=`), если нет активного X-Impersonate header. */
  organization_id?: number | null;
};

export type AdminTrainerMutationContext = {
  organization_id?: number | null;
};

function appendOrganizationScope(sp: URLSearchParams, organizationId: number | null | undefined): void {
  if (organizationId != null && organizationId > 0) {
    sp.set('organization_id', String(organizationId));
  }
}

function buildOrgScopeQuery(ctx: AdminTrainerMutationContext = {}): string {
  const sp = new URLSearchParams();
  appendOrganizationScope(sp, ctx.organization_id);
  const s = sp.toString();
  return s ? `?${s}` : '';
}

/** `GET /api/v1/admin/trainers` — каталог тренеров org (`staff:org_directory` + модуль fitness). */
export async function fetchAdminTrainersList(
  client: ApiClient,
  params: FetchAdminTrainersParams = {}
): Promise<{ items: AdminTrainerRow[]; total: number }> {
  const sp = new URLSearchParams();
  const limit = params.limit ?? 20;
  const offset = params.offset ?? 0;
  sp.set('limit', String(limit));
  sp.set('offset', String(offset));
  if (params.q?.trim()) sp.set('q', params.q.trim());
  appendOrganizationScope(sp, params.organization_id);
  const raw = await client.request<unknown>(`/admin/trainers?${sp.toString()}`, { method: 'GET' });
  return normalizeListPayload(raw);
}

/** `GET /api/v1/admin/trainers/{trainer_id}` — карточка тренера (модуль fitness). */
export async function fetchAdminTrainerById(
  client: ApiClient,
  trainerId: number,
  ctx: AdminTrainerMutationContext = {}
): Promise<AdminTrainerRow | null> {
  const raw = await client.request<unknown>(`/admin/trainers/${trainerId}${buildOrgScopeQuery(ctx)}`, { method: 'GET' });
  return parseAdminTrainerRow(raw);
}

export type TrainerPhoneHintsParams = {
  /** Префикс цифр (как ожидает бэкенд: query `prefix`). */
  prefix: string;
  limit?: number;
  organization_id?: number | null;
};

/** `GET /api/v1/admin/trainers/phone-hint` — подсказки по телефонам staff (не client). */
export async function fetchTrainerPhoneHints(
  client: ApiClient,
  params: TrainerPhoneHintsParams
): Promise<string[]> {
  const sp = new URLSearchParams();
  sp.set('prefix', params.prefix);
  if (params.limit != null && params.limit > 0) sp.set('limit', String(params.limit));
  appendOrganizationScope(sp, params.organization_id);
  const raw = await client.request<unknown>(`/admin/trainers/phone-hint?${sp.toString()}`, { method: 'GET' });
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return [];
  const hints = (raw as Record<string, unknown>).hints;
  if (!Array.isArray(hints)) return [];
  return hints.map((h) => String(h).trim()).filter(Boolean);
}

export type CreateAdminTrainerBody =
  | {
      user_id: number;
      branch_id: number;
      specialization?: string | null;
      description?: string | null;
      photo_url?: string | null;
    }
  | {
      full_name: string;
      email?: string;
      phone: string;
      branch_id: number;
      specialization?: string | null;
      description?: string | null;
      photo_url?: string | null;
    };

/** `POST /api/v1/admin/trainers` — `fitness:manage`. */
export async function createAdminTrainer(
  client: ApiClient,
  body: CreateAdminTrainerBody,
  ctx: AdminTrainerMutationContext = {}
): Promise<AdminTrainerRow> {
  const raw = await client.request<unknown>(`/admin/trainers${buildOrgScopeQuery(ctx)}`, { method: 'POST', body });
  const row = parseAdminTrainerRow(raw);
  if (!row) throw new Error('Пустой ответ при создании тренера');
  return row;
}

/** `PATCH /api/v1/admin/trainers/{id}` — `fitness:manage`. */
export async function patchAdminTrainer(
  client: ApiClient,
  trainerId: number,
  body: Record<string, unknown>,
  ctx: AdminTrainerMutationContext = {}
): Promise<AdminTrainerRow> {
  const raw = await client.request<unknown>(`/admin/trainers/${trainerId}${buildOrgScopeQuery(ctx)}`, { method: 'PATCH', body });
  const row = parseAdminTrainerRow(raw);
  if (!row) throw new Error('Пустой ответ при сохранении тренера');
  return row;
}

export type CreateAdminTrainerPlacementBody = {
  branch_id: number;
};

/** `POST /api/v1/admin/trainers/{id}/placements` — новая привязка к филиалу (`fitness:manage`). */
export async function createAdminTrainerPlacement(
  client: ApiClient,
  trainerId: number,
  body: CreateAdminTrainerPlacementBody,
  ctx: AdminTrainerMutationContext = {}
): Promise<TrainerBranchPlacement> {
  const raw = await client.request<unknown>(`/admin/trainers/${trainerId}/placements${buildOrgScopeQuery(ctx)}`, { method: 'POST', body });
  const row = parsePlacement(raw);
  if (!row) throw new Error('Пустой ответ при привязке к филиалу');
  return row;
}

/** `DELETE /api/v1/admin/trainers/{id}/placements/{placement_id}` — снятие с филиала (`fitness:manage`). */
export async function deleteAdminTrainerPlacement(
  client: ApiClient,
  trainerId: number,
  placementId: number,
  ctx: AdminTrainerMutationContext = {}
): Promise<void> {
  await client.request<unknown>(`/admin/trainers/${trainerId}/placements/${placementId}${buildOrgScopeQuery(ctx)}`, { method: 'DELETE' });
}

/** `DELETE /api/v1/admin/trainers/{id}` — снятие тренерской роли (`fitness:manage`). Тело только при ошибке. */
export async function deleteAdminTrainer(
  client: ApiClient,
  trainerId: number,
  ctx: AdminTrainerMutationContext = {}
): Promise<void> {
  await client.request<unknown>(`/admin/trainers/${trainerId}${buildOrgScopeQuery(ctx)}`, { method: 'DELETE' });
}
